/**
 * (B) Where a road marking is allowed to be painted.
 *
 * A junction's stop lines and a level crossing's barrier arms were drawn to the road's *declared* width — the number in
 * `content/roads/*.json` — which is what the line asks for, not what actually got laid. Wherever the surface is
 * narrower than that, or stops early, or was never there at all, the marking ran on out over the grass and the gravel
 * (owner, Sep 8, with a screenshot of a rural crossing).
 *
 * So: walk the span a tile at a time and keep only the stretches that really are carriageway. Kept out of renderer.ts
 * because the rule is worth testing on its own — the renderer's own output is rain, traffic and a moving clock, and
 * comparing two frames of that proves nothing.
 */

/** Is this tile road you can drive on? (a pavement is not — a stop line across the kerb looks just as wrong) */
export type Paved = (x: number, y: number) => boolean;

/**
 * The sub-stretches of `[a, b]` along `axis` — 'h' walks x at a fixed y, 'v' walks y at a fixed x — that are paved.
 * Coordinates are continuous and the ends are clamped to the span asked for, so a run never reaches past it.
 */
export function pavedRuns(paved: Paved, axis: 'h' | 'v', u: number, a: number, b: number): [number, number][] {
  const lo = Math.min(a, b), hi = Math.max(a, b);
  if (!(hi > lo)) return [];
  const out: [number, number][] = [];
  let from: number | null = null;
  for (let t = Math.floor(lo); t < Math.ceil(hi); t++) {
    const on = axis === 'h' ? paved(t + 0.5, u) : paved(u, t + 0.5);
    if (on) { if (from === null) from = t; }
    else if (from !== null) { out.push([from, t]); from = null; }
  }
  if (from !== null) out.push([from, Math.ceil(hi)]);
  return out.map(([p, q]) => [Math.max(lo, p), Math.min(hi, q)] as [number, number]).filter(([p, q]) => q - p > 0.01);
}

/**
 * The one stretch of road a crossing sits on, for a barrier arm that must reach across it and no further.
 * `centre` is where the crossing is; a gap right at the centre (an unpaved tile) means there is nothing to bar.
 */
export function pavedSpanAt(paved: Paved, axis: 'h' | 'v', u: number, centre: number, reach: number): [number, number] | null {
  return pavedRuns(paved, axis, u, centre - reach, centre + reach).find(([p, q]) => centre >= p - 0.5 && centre <= q + 0.5) ?? null;
}
